import { AfterViewInit, Component, DestroyRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { LoadingOptions } from '../../../../../../models/shared/loading-options';
import { AuthCodeRequest } from '../../../../../../models/users/requests/auth-code-request';
import { ButtonTypeEnum } from '../../../../../../ui/buttons/button/button-type.enum';
import { InternalUserAuthViewModel } from '../internal-user-auth-view-model.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SessionService } from '../../../../../../services/session-service';
import { InternalUser } from '../../../../../../models/users/dto/internal-user';
import { TextInputComponent } from '../../../../../../ui/form-components/text-input/text-input.component';

@Component({
  selector: 'app-internal-user-verify-code',
  templateUrl: './internal-user-verify-code.component.html',
  styleUrls: ['./internal-user-verify-code.component.scss', '../../auth.component.scss']
})
export class InternalUserVerifyCodeComponent implements AfterViewInit {
  private viewModel = inject(InternalUserAuthViewModel);
  private sessionService = inject(SessionService);
  private destroyRef = inject(DestroyRef);

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  @ViewChild('textInput', { static: true }) textInput!: TextInputComponent;

  private listenToLoadingOpts = this.viewModel.loadingOpts$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(opts => this.loadingOpts.emit(opts));

  public formSubmitted: boolean = false;

  public formGroup = new FormGroup({
    timeBasedOneTimePassword: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(6), Validators.maxLength(6)]
    })
  });

  public authFlowTitle$ = this.viewModel.authFlowTitle$;
  public authFlowSubtext$ = this.viewModel.authFlowSubtext$;

  public customErrorMessages = {
    timeBasedOneTimePassword: {
      required: $localize`Verification code is required`,
      minlength: $localize`Verification code must be 6 digits`,
      maxlength: $localize`Verification code must be 6 digits`
    }
  };

  public ngAfterViewInit(): void {
    this.textInput.input.nativeElement.focus();
  }

  public verifyMfaCode(): void {
    this.formSubmitted = true;
    if (!this.formGroup.valid) return;
    this.sessionService.sessionContainer$.pipe(take(1)).subscribe(session => {
      if (!session) session = this.viewModel.getCachedSession();
      const user = session?.user as InternalUser;
      const { timeBasedOneTimePassword } = this.formGroup.getRawValue();
      const req = new AuthCodeRequest(
        user?.email ?? '',
        timeBasedOneTimePassword,
        user?.challengeName ?? null,
        user?.sessionId ?? ''
      );
      this.viewModel.verifyMfaCode(req, false);
    });
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
